import React, { useMemo, useState, useEffect } from 'react';
import LoginDemo from './components/LoginDemo';
import ListaAcusados from './components/ListaAcusados';
import KanbanAcusados from './components/KanbanAcusados';
import Configuracion from './components/Configuracion';
import RegistrarAcusado from './components/RegistrarAcusado';

import MenuNav from './components/MenuNav';
import Reportes from './components/Reportes';
import DetalleAcusadoPage from './components/DetalleAcusadoPage';
import GestionPlantillas from './components/GestionPlantillas';
import GestionComunicacional from './components/GestionComunicacional'; 
import { GestureNav } from './components/GestureNav';
import { HASURA_URL, ENABLE_LOG } from './config';

// Apollo Client Imports
import { ApolloClient, InMemoryCache, ApolloProvider } from '@apollo/client';
import { HttpLink } from '@apollo/client/link/http';
import { setContext } from '@apollo/client/link/context'; // New for JWT

const LOG_ENABLED = ENABLE_LOG;

function App() {
  const [usuario, setUsuario] = useState(null);
  const [modulo, setModulo] = useState('dashboard'); 
  
  // Judicial State
  const [vista, setVista] = useState('kanban');
  const [history, setHistory] = useState([]); 
  const [acusados, setAcusados] = useState([]); 
  const [acusadoActivo, setAcusadoActivo] = useState(null);

  // Check for existing session on mount
  useEffect(() => {
    const savedUser = localStorage.getItem('user_session');
    if (savedUser) {
      try {
        setUsuario(JSON.parse(savedUser));
      } catch (e) {
        localStorage.removeItem('user_session');
        localStorage.removeItem('jwt_token');
      }
    }
  }, []);

  const handleLogin = (userData) => {
    setUsuario(userData);
    localStorage.setItem('user_session', JSON.stringify(userData));
    if (userData.token) {
      localStorage.setItem('jwt_token', userData.token);
    }
  };

  const handleLogout = () => {
    setUsuario(null);
    localStorage.removeItem('user_session');
    localStorage.removeItem('jwt_token');
    setModulo('dashboard');
    setVista('kanban');
  };

  const apolloClient = useMemo(() => {
    let hasuraUrl = HASURA_URL || 'http://localhost:8080';
    hasuraUrl = hasuraUrl.replace(/\/$/, ''); // Limpiar barra final
    const hasuraEndpoint = `${hasuraUrl}/v1/graphql`;

    if (LOG_ENABLED) console.log('🔗 Conexión GraphQL Establecida');

    const authLink = setContext((_, { headers }) => {
      const token = localStorage.getItem('jwt_token');
      const isRealToken = token && token.split('.').length === 3;

      return {
        headers: {
          ...headers,
          ...(isRealToken ? { authorization: `Bearer ${token}` } : {}),
        },
      };
    });

    const httpLink = new HttpLink({
      uri: hasuraEndpoint,
    });

    return new ApolloClient({
      link: authLink.concat(httpLink),
      cache: new InMemoryCache(),
    });
  }, []);

  const goTo = (nextView) => {
    setHistory(prev => [...prev, vista]);
    setVista(nextView);
  };

  const handleVolver = () => {
    setHistory(prev => {
      if (prev.length === 0) { setVista('kanban'); return prev; }
      const copy = [...prev];
      const backTo = copy.pop();
      setVista(backTo || 'kanban');
      return copy;
    });
  };

  const handleActualizarAcusado = (actualizado) => {
    setAcusados(prev => prev.map(a => a.acusado_id === actualizado.acusado_id ? { ...a, ...actualizado } : a));
    setAcusadoActivo(prev => prev && prev.acusado_id === actualizado.acusado_id ? { ...prev, ...actualizado } : prev);
  };

  // Gesture Navigation Handlers
  const handleBackGesture = () => {
    if (modulo === 'judicial' && vista !== 'kanban') {
      handleVolver(); 
    } else if (modulo === 'comunicacional' && vista !== 'dashboard') {
      setVista('dashboard'); 
    } else if (modulo !== 'dashboard') {
      setModulo('dashboard'); 
    }
  };

  const handleRefreshGesture = async () => {
    if (LOG_ENABLED) console.log("Simulating data refresh...");
    await new Promise(resolve => setTimeout(resolve, 1500));
    if (LOG_ENABLED) console.log("Data refreshed!");
  };

  // Login Screen
  if (!usuario) {
    return (
      <ApolloProvider client={apolloClient}>
        <LoginDemo onLogin={handleLogin} />
      </ApolloProvider>
    );
  }

  // Final UI Logic
  let mainContent = null;

  if (modulo === 'dashboard') {
    mainContent = (
      <GestureNav onBack={handleBackGesture} onRefresh={handleRefreshGesture}>
        <div className="container d-flex flex-column justify-content-center align-items-center vh-100 bg-light">
          <div className="text-center mb-5">
            <h1 className="fw-bold text-dark mb-2">🏛️ Sistema Integrado</h1>
            <p className="text-muted">Justicia Militar & Gestión Administrativa</p>
          </div>
          
          <div className="row g-4 w-100 justify-content-center" style={{maxWidth: '1000px'}}>
            <div className="col-md-6 col-lg-5">
              <div className="card shadow-sm border-0 mb-3" onClick={() => setModulo('judicial')} style={{cursor: 'pointer', borderRadius: '12px'}}>
                <div className="card-body p-3 d-flex align-items-center">
                  <div className="bg-primary bg-opacity-10 rounded-3 me-3 d-flex align-items-center justify-content-center" style={{width: '48px', height: '48px', minWidth: '48px'}}>
                    <i className="bi bi-bank fs-4 text-primary"></i>
                  </div>
                  <div className="flex-grow-1 text-start overflow-hidden">
                    <h6 className="fw-bold mb-0 text-dark text-truncate">Gestión Judicial</h6>
                    <p className="text-muted small mb-0 text-truncate">Tribunales y Acusados</p>
                  </div>
                  <i className="bi bi-chevron-right text-muted small ms-2"></i>
                </div>
              </div>
            </div>

            <div className="col-md-6 col-lg-5">
              <div className="card shadow-sm border-0 mb-3" onClick={() => setModulo('comunicacional')} style={{cursor: 'pointer', borderRadius: '12px'}}>
                <div className="card-body p-3 d-flex align-items-center">
                  <div className="bg-success bg-opacity-10 rounded-3 me-3 d-flex align-items-center justify-content-center" style={{width: '48px', height: '48px', minWidth: '48px'}}>
                    <i className="bi bi-megaphone fs-4 text-success"></i>
                  </div>
                  <div className="flex-grow-1 text-start overflow-hidden">
                    <h6 className="fw-bold mb-0 text-dark text-truncate">Gestión Comunicacional</h6>
                    <p className="text-muted small mb-0 text-truncate">RRHH y Personal</p>
                  </div>
                  <i className="bi bi-chevron-right text-muted small ms-2"></i>
                </div>
              </div>
            </div>
          </div>
          
          <div className="mt-5 text-muted small">
            Logueado como: <strong>{usuario.name || 'Usuario'}</strong> • 
            <button className="btn btn-link btn-sm text-danger text-decoration-none ms-1" onClick={handleLogout}>Salir</button>
          </div>
        </div>
      </GestureNav>
    );
  } else if (modulo === 'comunicacional') {
    mainContent = (
      <GestureNav onBack={handleBackGesture} onRefresh={handleRefreshGesture}>
        <GestionComunicacional onVolver={() => setModulo('dashboard')} />
      </GestureNav>
    );
  } else {
    // Judicial Module
    let judicialContenido = null;
    if (vista === 'acusados') {
      judicialContenido = <ListaAcusados acusados={acusados} setAcusados={setAcusados} onAbrirDetalle={(a) => { setAcusadoActivo(a); goTo('detalle'); }} onIrRegistrar={() => goTo('registrar')} onVolver={handleVolver} />;
    } else if (vista === 'kanban') {
      judicialContenido = (
        <KanbanAcusados
          acusados={acusados}
          onAbrirDetalle={(a) => { setAcusadoActivo(a); goTo('detalle'); }}
          onActualizarAcusado={handleActualizarAcusado}
          onIrRegistrar={() => goTo('registrar')}
          onSetAcusados={setAcusados}
        />
      );
    } else if (vista === 'reportes') {
      judicialContenido = <Reportes acusados={acusados} onVolver={handleVolver} />;
    } else if (vista === 'config') {
      judicialContenido = <Configuracion onVolver={handleVolver} />;
    } else if (vista === 'registrar') {
      const grados = ['Teniente','Sargento','Capitán','Coronel','General'];
      judicialContenido = <RegistrarAcusado grados={grados} onAgregarAcusado={nuevo => { setAcusados([...acusados, nuevo]); handleVolver(); }} onVolver={handleVolver} />;
    } else if (vista === 'formatos') {
      judicialContenido = <GestionPlantillas onVolver={handleVolver} />;
    } else if (vista === 'detalle' && acusadoActivo) {
      judicialContenido = <DetalleAcusadoPage acusado={acusadoActivo} onVolver={handleVolver} onActualizarAcusado={handleActualizarAcusado} />;
    } else {
      judicialContenido = <ListaAcusados acusados={acusados} setAcusados={setAcusados} />;
    }

    mainContent = (
      <GestureNav onBack={handleBackGesture} onRefresh={handleRefreshGesture}>
        <div className="bg-dark text-white px-3 py-2 d-flex justify-content-between align-items-center small shadow-sm">
          <span className="fw-bold"><i className="bi bi-bank me-2"></i>Sistema Judicial</span>
          <button className="btn btn-outline-light btn-sm rounded-pill px-3" onClick={() => setModulo('dashboard')}>
            <i className="bi bi-grid-fill me-1"></i>Menú Principal
          </button>
        </div>
        <MenuNav onNav={goTo} usuario={usuario} />
        <div style={{ minHeight: '80vh' }}>{judicialContenido}</div>
        <div className="text-center text-muted py-3 small">&copy; {new Date().getFullYear()} Icarosoft Legal</div>
      </GestureNav>
    );
  }

  return (
    <ApolloProvider client={apolloClient}>
      {mainContent}
    </ApolloProvider>
  );
}

export default App;
